import React, { useState, useRef, useEffect, useContext } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';

import '../index.css';

import { DataContext } from '../Components/DataContext';

import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import WavyLine from '../Components/wavyline';
import { FaPhone, FaArrowRight, FaCheckCircle, FaClock, FaUserMd, FaHeart, FaStar } from 'react-icons/fa';

function ServicePage() {
  const data = useContext(DataContext);
  const [selectedService, setSelectedService] = useState(null);
  const [descriptionHeights, setDescriptionHeights] = useState({});
  const descriptionRefs = useRef({});
  const { scrollYProgress } = useScroll();
  
  // Parallax transforms
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  
  // Smooth spring animations
  const springY1 = useSpring(y1, { stiffness: 100, damping: 30 });
  const springY2 = useSpring(y2, { stiffness: 100, damping: 30 });
  const springY3 = useSpring(y3, { stiffness: 100, damping: 30 });

  // Scroll progress indicator
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Calculate description heights for animations
  useEffect(() => {
    if (data?.PodService && data.PodService.length > 0) {
      const services = data.PodService;
      const heights = {};
      services.forEach(service => {
        if (descriptionRefs.current[service.id]) {
          heights[service.id] = descriptionRefs.current[service.id].scrollHeight;
        } else {
          heights[service.id] = 0;
        }
      });
      setDescriptionHeights(heights);
    }
  }, [selectedService, data?.PodService]);

  useEffect(() => {
    if (selectedService && descriptionRefs.current[selectedService]) {
      const timer = setTimeout(() => {
        if (descriptionRefs.current[selectedService]) {
          setDescriptionHeights(prevHeights => ({
            ...prevHeights,
            [selectedService]: descriptionRefs.current[selectedService].scrollHeight
          }));
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [selectedService]);

  // Handle loading state
  if (!data || !data.PodService || !data.ServicesPage) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  const services = data.PodService;
  const { hero, intro, banner, callToAction } = data.ServicesPage;

  // SEO structured data for Services page
  const servicesPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Podiatry Services Manchester",
    "description": "Professional podiatry services in Manchester including foot care, foot pain treatment, nail treatment, fungal nail management, and corn and callus removal.",
    "url": "https://www.droshpod.com/services",
    "provider": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Manchester",
        "addressRegion": "Greater Manchester",
        "addressCountry": "GB"
      }
    },
    "serviceType": [
      "Routine Appointment",
      "Foot Pain Treatment", 
      "Home Visits",
      "Nail Treatment",
      "Fungal Nail Management",
      "Corn and Callus Removal"
    ]
  };

  const handleServiceChange = (serviceId) => {
    setSelectedService(prevSelected => prevSelected === serviceId ? null : serviceId);
  };

  return (
    <>
      <SEOHead 
        title="Podiatry Services Manchester | Foot Care & Treatment"
        description="Professional podiatry services in Manchester. Expert foot care, foot pain treatment, nail treatment, fungal nail management, and corn and callus removal. Book your appointment today."
        keywords="podiatry services manchester, foot care, foot pain treatment, nail treatment, fungal nail management, corn removal, callus removal, podiatrist manchester, foot treatment"
        canonical="https://www.droshpod.com/services"
        structuredData={servicesPageStructuredData}
      />
      
      <main className="relative overflow-hidden min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Scroll Progress Bar */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-primary origin-left z-50"
          style={{ scaleX }}
        />

        {/* Floating Background Elements */}
        <motion.div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{ y: springY1 }}
        >
          <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-secondary/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-primary/5 rounded-full blur-xl"></div>
        </motion.div>

        {/* Parallax Background Elements */}
        <motion.div 
          className="absolute inset-0 pointer-events-none"
          style={{ y: springY2 }}
        >
          <div className="absolute top-1/4 right-10 w-32 h-32 border border-primary/20 rounded-full"></div>
          <div className="absolute bottom-1/3 left-10 w-24 h-24 border border-secondary/20 rounded-full"></div>
        </motion.div>

        {/* Wavy Background with Parallax */}
        <motion.div 
          className="absolute inset-0 top-1/2 overflow-hidden -z-10"
          style={{ y: springY3 }}
        >
          <WavyLine />
        </motion.div>

        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-6 relative z-10">
          <PageBreadcrumbs />
        </div>

        {/* Hero Section */}
        <section className="relative z-10 py-10 md:py-12">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center mb-8"
            >
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
                {hero.title}
              </h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {intro.description || hero.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Lunula Laser Promotional Banner - Top */}
        <section className="py-4 md:py-6 relative z-10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Link 
                to="/lunular-laser"
                className="block group"
              >
                <div className="relative bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="flex-1 text-center md:text-left">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-medium">
                          Premium Treatment
                        </p>
                        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
                          Lunula Laser Treatment
                        </h2>
                        <p className="text-base md:text-lg text-gray-600 mb-4 max-w-2xl">
                          Painless, effective treatment for fungal nails. Grow new, healthy nails with our advanced laser technology.
                        </p>
                        <div className="flex justify-center md:justify-start">
                          <span className="text-primary font-medium text-sm inline-flex items-center group-hover:gap-2 transition-all duration-300">
                            Learn more
                            <FaArrowRight className="ml-1 text-xs group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-8 md:py-10 bg-white/50 backdrop-blur-sm relative z-10">
          <div className="container mx-auto px-4">

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={service.serviceImage}
                      alt={`${service.serviceTitle} - Podiatry Service`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {service.serviceTitle}
                      </h3>
                      <p className="text-white/90 text-sm">
                        From {service.serviceCost}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                      {service.serviceDescription}
                    </p>
                    <button
                      onClick={() => handleServiceChange(service.id)}
                      className="text-secondary hover:text-primary font-semibold flex items-center transition-colors"
                    >
                      Learn More
                      <FaArrowRight className="ml-2 text-sm" />
                    </button>
                  </div>

                  {/* Expandable Description */}
                  <AnimatePresence>
                    {selectedService === service.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: descriptionHeights[service.id] || 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div
                          ref={el => descriptionRefs.current[service.id] = el}
                          className="px-6 pb-6"
                        >
                          <div className="border-t border-gray-200 pt-4">
                            <h4 className="font-semibold text-gray-800 mb-2">
                              What's Included:
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                              <li className="flex items-center">
                                <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                                Professional assessment and diagnosis
                              </li>
                              <li className="flex items-center">
                                <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                                Personalised treatment plan
                              </li>
                              <li className="flex items-center">
                                <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                                Expert care and treatment
                              </li>
                              <li className="flex items-center">
                                <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                                Follow-up care instructions
                              </li>
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>


        {/* Why Choose Us Section */}
        <section className="py-10 md:py-12 bg-white/60 backdrop-blur-sm relative z-10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                Why Choose Our Podiatry Services?
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                Experience the difference that professional, caring podiatry services can make to your foot health.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center p-5 rounded-lg hover:shadow-md transition-shadow duration-300 bg-white/80"
              >
                <FaUserMd className="text-3xl text-secondary mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-2">Expert Care</h3>
                <p className="text-gray-600 text-sm">Qualified and experienced podiatrists with specialised training.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center p-5 rounded-lg hover:shadow-md transition-shadow duration-300 bg-white/80"
              >
                <FaHeart className="text-3xl text-red-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-2">Patient-Focused</h3>
                <p className="text-gray-600 text-sm">Personalised treatment plans designed around your needs.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center p-5 rounded-lg hover:shadow-md transition-shadow duration-300 bg-white/80"
              >
                <FaClock className="text-3xl text-purple-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-2">Convenient Hours</h3>
                <p className="text-gray-600 text-sm">Flexible appointment times to fit your busy schedule.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Banner Section */}
        <section className="py-10 md:py-12 bg-gradient-to-r from-secondary to-primary text-white relative z-10">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                {banner.title}
              </h2>
              <p className="text-base md:text-lg mb-6 opacity-90">
                {banner.description}
              </p>
              <Link 
                to={banner.buttonLink}
                className="bg-white text-secondary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300 inline-flex items-center"
              >
                {banner.buttonText}
                <FaArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-10 md:py-12 bg-white/50 backdrop-blur-sm relative z-10">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                {callToAction.title}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6">
                {callToAction.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to={callToAction.buttonLink}
                  className="bg-secondary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary transition-colors duration-300"
                >
                  {callToAction.buttonText}
                </Link>
                <a 
                  href="tel:07809267586"
                  className="border-2 border-secondary text-secondary px-8 py-3 rounded-full font-semibold hover:bg-secondary hover:text-white transition-colors duration-300 flex items-center justify-center"
                >
                  <FaPhone className="mr-2" />
                  Call Us
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default ServicePage;